import { useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { calculateTotals } from '../lib/utils';

/**
 * Hook pour générer des PDFs de devis/factures
 * @param {Object} params - Paramètres du document
 * @returns {Object} - { generatePDF, urlToBase64 }
 */
export function usePDFGeneration({ docInfo, sections, client, globalSettings, companyInfo, type }) {

  // Convertir une URL en base64 via Cloud Function
  const urlToBase64 = useCallback(async (url) => {
    try {
      const functions = getFunctions();
      const convertImage = httpsCallable(functions, 'convertImageToBase64');
      const result = await convertImage({ imageUrl: url });

      if (result.data.success) {
        return result.data.base64;
      } else {
        console.error('Erreur conversion signature:', result.data);
        return null;
      }
    } catch (error) {
      console.error('Erreur Cloud Function signature:', error);
      return null;
    }
  }, []);

  // Formater un nombre pour le PDF (style français)
  const pdfFormat = useCallback((value, withSymbol = true) => {
    try {
      const num = Number(value) || 0;
      const parts = num.toFixed(2).split('.');
      let intPart = parts[0];
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const formatted = `${intPart},${parts[1]}`;
      return withSymbol ? `${formatted} €` : formatted;
    } catch (e) {
      return String(value);
    }
  }, []);

  // Générer le PDF
  const generatePDF = useCallback(async () => {
    const totals = calculateTotals(sections, globalSettings);
    const doc = new jsPDF({ unit: 'pt' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let cursorY = margin;

    // Bande colorée en haut
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 8, 'F');
    cursorY += 20;

    // Header: logo + company info
    if (companyInfo.logoUrl) {
      try {
        doc.addImage(companyInfo.logoUrl, 'PNG', margin, cursorY, 100, 50);
      } catch (e) {
        try {
          doc.addImage(companyInfo.logoUrl, 'JPEG', margin, cursorY, 100, 50);
        } catch (e2) { /* ignore */ }
      }
    }

    const companyX = margin + (companyInfo.logoUrl ? 115 : 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(companyInfo.name || '', companyX, cursorY + 14);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const infoLines = [companyInfo.address, companyInfo.city, companyInfo.phone, companyInfo.email].filter(Boolean);
    infoLines.forEach((l, i) => doc.text(l, companyX, cursorY + 32 + i * 12));

    // Client box
    const clientBoxWidth = 220;
    const clientBoxX = pageWidth - margin - clientBoxWidth;

    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({ opacity: 0.05 }));
    doc.roundedRect(clientBoxX + 2, cursorY + 2, clientBoxWidth, 75, 8, 8, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(clientBoxX, cursorY, clientBoxWidth, 75, 8, 8, 'FD');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('CLIENT', clientBoxX + 14, cursorY + 16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(client.name || '', clientBoxX + 14, cursorY + 32);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    const clientLines = [client.address || '', client.city || ''].filter(Boolean);
    clientLines.forEach((l, i) => doc.text(l, clientBoxX + 14, cursorY + 46 + i * 12));

    cursorY += 100;

    // Ligne de séparation
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 25;

    // Title avec badge
    const titleText = type === 'quote' ? 'DEVIS' : 'FACTURE';
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(margin, cursorY - 8, 100, 26, 4, 4, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(titleText, margin + 50, cursorY + 8, { align: 'center' });

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`N° ${docInfo.number}`, margin + 110, cursorY + 8);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Date : ${docInfo.date}`, pageWidth - margin, cursorY + 8, { align: 'right' });

    // Date de validité du devis
    if (type === 'quote' && companyInfo.quoteValidityDays) {
      const dateParts = docInfo.date.split('/');
      const validityDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
      validityDate.setDate(validityDate.getDate() + (companyInfo.quoteValidityDays || 30));
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(71, 85, 105);
      doc.text(`Valable jusqu'au : ${validityDate.toLocaleDateString('fr-FR')}`, pageWidth - margin, cursorY + 22, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
    }

    if (docInfo.projectAddress) {
      doc.text(`Chantier : ${docInfo.projectAddress}`, pageWidth - margin, cursorY + (type === 'quote' && companyInfo.quoteValidityDays ? 36 : 22), { align: 'right' });
    }

    if (companyInfo.defaultExecutionDelay) {
      const execY = cursorY + (docInfo.projectAddress ? 50 : 36);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      doc.text(`Délai d'exécution : ${companyInfo.defaultExecutionDelay}`, pageWidth - margin, execY, { align: 'right' });
    }

    cursorY += 40;

    // Table body
    const tableBody = [];
    sections.forEach(section => {
      tableBody.push([{ content: section.title.toUpperCase(), colSpan: 5, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
      section.items.forEach(item => {
        tableBody.push([
          { content: String(item.desc || ''), styles: { cellPadding: 4 } },
          { content: String(item.qty || ''), styles: { halign: 'center' } },
          { content: String(item.unit || ''), styles: { halign: 'center' } },
          { content: pdfFormat(item.price).replace('€', '').trim(), styles: { halign: 'right' } },
          { content: pdfFormat((item.qty || 0) * (item.price || 0)).replace('€', '').trim(), styles: { halign: 'right' } }
        ]);
      });
    });

    autoTable(doc, {
      startY: cursorY,
      head: [['Désignation', 'Qté', 'Unité', 'PU HT', 'Total HT']],
      body: tableBody,
      theme: 'striped',
      styles: {
        fontSize: 9,
        cellPadding: 8,
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      headStyles: {
        fillColor: [51, 65, 85],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 45, halign: 'center' },
        2: { cellWidth: 45, halign: 'center' },
        3: { cellWidth: 80, halign: 'right' },
        4: { cellWidth: 80, halign: 'right', fontStyle: 'bold' }
      },
    });

    let finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 20 : cursorY + 20;

    // Vérifier l'espace restant
    const requiredSpace = 350;
    const availableSpace = doc.internal.pageSize.getHeight() - finalY - 60;
    if (availableSpace < requiredSpace) {
      doc.addPage();
      finalY = 40;
    }

    // Totals box
    const totalsBoxWidth = 240;
    const totalsX = pageWidth - margin - totalsBoxWidth;

    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({ opacity: 0.08 }));
    doc.roundedRect(totalsX + 3, finalY + 3, totalsBoxWidth, 85, 8, 8, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(totalsX, finalY, totalsBoxWidth, 85, 8, 8, 'FD');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Total HT', totalsX + 16, finalY + 20);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.rawTotalHT), totalsX + totalsBoxWidth - 16, finalY + 20, { align: 'right' });

    doc.setTextColor(71, 85, 105);
    doc.text(`TVA (${companyInfo.defaultVat}%)`, totalsX + 16, finalY + 40);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.totalTVA), totalsX + totalsBoxWidth - 16, finalY + 40, { align: 'right' });

    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1);
    doc.line(totalsX + 16, finalY + 52, totalsX + totalsBoxWidth - 16, finalY + 52);

    doc.setFillColor(37, 99, 235);
    doc.roundedRect(totalsX + 10, finalY + 60, totalsBoxWidth - 20, 18, 4, 4, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('NET À PAYER', totalsX + 16, finalY + 72);
    doc.setFontSize(13);
    doc.text(pdfFormat(totals.totalTTC), totalsX + totalsBoxWidth - 16, finalY + 72, { align: 'right' });

    // Signatures
    const sigY = finalY + 110;
    const sigWidth = 230;
    const sigHeight = 75;
    const gap = 35;
    const leftX = margin;
    const rightX = margin + sigWidth + gap;

    // Client signature
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE CLIENT', leftX, sigY - 10);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text('Bon pour accord - Lu et approuvé', leftX, sigY - 2);

    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftX, sigY, sigWidth, sigHeight, 6, 6, 'FD');

    let clientSignature = docInfo.signatureUrl || docInfo.signature;
    if (clientSignature && clientSignature.startsWith('http')) {
      clientSignature = await urlToBase64(clientSignature);
    }

    if (clientSignature) {
      try {
        doc.addImage(clientSignature, 'PNG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10);
      } catch (e) {
        try {
          doc.addImage(clientSignature, 'JPEG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10);
        } catch (e2) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', leftX + sigWidth / 2, sigY + sigHeight / 2, { align: 'center' });
        }
      }

      if (docInfo.signed && docInfo.signedBy && docInfo.signedAt) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(34, 197, 94);
        const signedDate = docInfo.signedAt.toDate ? docInfo.signedAt.toDate() : new Date(docInfo.signedAt);
        const dateStr = signedDate.toLocaleDateString('fr-FR');
        const timeStr = signedDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        doc.text(`Signé par ${docInfo.signedBy} le ${dateStr} à ${timeStr}`, leftX + sigWidth / 2, sigY + sigHeight + 8, { align: 'center' });
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', leftX + sigWidth / 2, sigY + sigHeight / 2, { align: 'center' });
    }

    // Company signature
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE ENTREPRISE', rightX, sigY - 10);

    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(rightX, sigY, sigWidth, sigHeight, 6, 6, 'FD');

    const companySig = companyInfo.signatureUrl || companyInfo.signature || companyInfo.companySignature;
    if (companySig) {
      try {
        doc.addImage(companySig, 'PNG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10);
      } catch (e) {
        try {
          doc.addImage(companySig, 'JPEG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10);
        } catch (e2) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', rightX + sigWidth / 2, sigY + sigHeight / 2, { align: 'center' });
        }
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', rightX + sigWidth / 2, sigY + sigHeight / 2, { align: 'center' });
    }

    // Payment info
    let paymentY = sigY + sigHeight + 20;

    doc.setFillColor(241, 245, 249);
    doc.rect(margin, paymentY, pageWidth - margin * 2, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    doc.text('CONDITIONS DE PAIEMENT', margin + 5, paymentY + 8);
    paymentY += 20;

    if (companyInfo.iban) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('Coordonnées bancaires :', margin, paymentY);
      paymentY += 12;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      if (companyInfo.bankName) {
        doc.text(`Banque : ${companyInfo.bankName}`, margin + 5, paymentY);
        paymentY += 10;
      }
      doc.text(`IBAN : ${companyInfo.iban}`, margin + 5, paymentY);
      paymentY += 10;
      if (companyInfo.bic) {
        doc.text(`BIC : ${companyInfo.bic}`, margin + 5, paymentY);
        paymentY += 10;
      }
      paymentY += 8;
    }

    if (companyInfo.paymentMethods) {
      const methods = [];
      if (companyInfo.paymentMethods.virement) methods.push('Virement bancaire');
      if (companyInfo.paymentMethods.cheque) methods.push('Chèque');
      if (companyInfo.paymentMethods.especes) methods.push('Espèces');
      if (companyInfo.paymentMethods.cb) methods.push('Carte bancaire');

      if (methods.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(71, 85, 105);
        doc.text('Modes de paiement acceptés :', margin, paymentY);
        paymentY += 12;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(methods.join(' • '), margin + 5, paymentY);
        paymentY += 15;
      }
    }

    if (companyInfo.paymentTermDays || companyInfo.latePenaltyText) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text('Conditions de règlement :', margin, paymentY);
      paymentY += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);

      if (companyInfo.paymentTermDays) {
        doc.text(`• Paiement à ${companyInfo.paymentTermDays} jours`, margin + 5, paymentY);
        paymentY += 9;
      }

      if (companyInfo.latePenaltyText) {
        const penaltyLines = doc.splitTextToSize(companyInfo.latePenaltyText, pageWidth - margin * 2 - 10);
        penaltyLines.forEach((line, idx) => {
          doc.text(idx === 0 ? `• ${line}` : `  ${line}`, margin + 5, paymentY);
          paymentY += 9;
        });
      }
      paymentY += 10;
    }

    if (docInfo.downPayment && docInfo.downPayment > 0) {
      const remaining = totals.totalTTC - docInfo.downPayment;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('Échéancier de paiement :', margin, paymentY);
      paymentY += 12;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`→ Acompte à la commande : ${docInfo.downPayment.toFixed(2)} €`, margin + 5, paymentY);
      paymentY += 10;
      doc.text(`→ Solde à la livraison : ${remaining.toFixed(2)} €`, margin + 5, paymentY);
      paymentY += 15;
    }

    if (companyInfo.insuranceName && companyInfo.insurancePolicy) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      doc.text(`Assurance : ${companyInfo.insuranceName} - Police N° ${companyInfo.insurancePolicy} - Zone : ${companyInfo.insuranceArea || 'France'}`, margin, paymentY);
      paymentY += 10;
    }

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 60;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);

    if (companyInfo.legalFooter) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const footerText = doc.splitTextToSize(companyInfo.legalFooter, pageWidth - margin * 2);
      doc.text(footerText, margin, footerY + 12);
    }

    doc.setFillColor(37, 99, 235);
    doc.rect(0, doc.internal.pageSize.getHeight() - 8, pageWidth, 8, 'F');

    // CGV page
    if (companyInfo.cgv && companyInfo.cgv.trim()) {
      doc.addPage();

      doc.setFillColor(241, 245, 249);
      doc.rect(0, 0, pageWidth, 50, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Conditions Générales de Vente', pageWidth / 2, 30, { align: 'center' });

      let cgvY = 60;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);

      const cgvLines = companyInfo.cgv.split('\n');
      cgvLines.forEach(line => {
        if (cgvY > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          cgvY = 20;
        }
        const wrappedLines = doc.splitTextToSize(line || ' ', pageWidth - margin * 2);
        doc.text(wrappedLines, margin, cgvY);
        cgvY += wrappedLines.length * 12;
      });

      doc.setFillColor(37, 99, 235);
      doc.rect(0, doc.internal.pageSize.getHeight() - 8, pageWidth, 8, 'F');
    }

    // Page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i}/${totalPages}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });
    }

    const pdfOutput = doc.output('datauristring');
    return pdfOutput.split(',')[1];
  }, [docInfo, sections, client, globalSettings, companyInfo, type, urlToBase64, pdfFormat]);

  return { generatePDF, urlToBase64 };
}

export default usePDFGeneration;
